import React, { useState } from 'react';
import axios from '../../axiosConfig';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/RegisterOwner.css';

const RegisterOwner = ({ handleSubmit: externalHandleSubmit, handleChange: externalHandleChange, error: externalError, success: externalSuccess, formData: externalFormData, fieldErrors: externalFieldErrors }) => {
    const [internalFormData, setInternalFormData] = useState({
        password: '', firstName: '', lastName: '',
        phone: '', country: '', city: '', postalCode: '',
        street: '', buildingNumber: '', apartmentNumber: '',
        statuteAccepted: false
    });

    const [internalError, setInternalError] = useState('');
    const [internalSuccess, setInternalSuccess] = useState('');
    const [internalFieldErrors, setInternalFieldErrors] = useState({});
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    // Use external props if provided (for RegisterInvitedTrainer), otherwise use internal state
    const formData = externalFormData || internalFormData;
    const setFormData = externalFormData ? () => {} : setInternalFormData;
    const error = externalError || internalError;
    const success = externalSuccess || internalSuccess;
    const fieldErrors = externalFieldErrors || internalFieldErrors;

    const handleChange = externalHandleChange || ((e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData({ ...formData, [e.target.name]: value });
        // Clear field error when user starts typing
        if (internalFieldErrors[e.target.name]) {
            setInternalFieldErrors({ ...internalFieldErrors, [e.target.name]: '' });
        }
    });

    const handleSubmit = externalHandleSubmit || (async (e) => {
        e.preventDefault();
        setInternalError('');
        setInternalSuccess('');
        setInternalFieldErrors({});

        if (!token) {
            setInternalError('Nieprawidłowy lub brakujący token rejestracyjny.');
            return;
        }

        const requestBody = {
            password: formData.password,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone,
            statuteAccepted: formData.statuteAccepted,
            address: {
                country: formData.country,
                city: formData.city,
                postalCode: formData.postalCode,
                street: formData.street,
                buildingNumber: formData.buildingNumber,
                apartmentNumber: formData.apartmentNumber
            }
        };

        try {
            await axios.post(`/api/v1/auth/register/owner?token=${token}`, requestBody);
            setInternalSuccess('Rejestracja powiodła się! Teraz możesz się zalogować.');
            setTimeout(() => navigate('/'), 2000);
        } catch (err) {
            if (err.response?.data?.errors) {
                // Handle field-specific validation errors
                const errors = err.response.data.errors;
                const mappedErrors = {};

                // Map backend field names to form field names
                Object.keys(errors).forEach(key => {
                    if (key.startsWith('address.')) {
                        // Handle nested address fields (e.g., address.city -> city)
                        const fieldName = key.replace('address.', '');
                        mappedErrors[fieldName] = errors[key];
                    } else {
                        mappedErrors[key] = errors[key];
                    }
                });

                setInternalFieldErrors(mappedErrors);
                setInternalError(err.response.data.message || 'Błąd walidacji');
            } else {
                setInternalError('Rejestracja nie powiodła się. Sprawdź wprowadzone dane i spróbuj ponownie.');
            }
        }
    });

    const handleBack = () => {
        navigate(-1);
    };

    if (!token && !externalHandleSubmit) {
        return (
            <div className="owner-registration">
                <div className="owner-registration__left-panel">
                    <div className="owner-registration__left-content">
                        <h2 className="owner-registration__welcome-text">
                            Witamy w
                            <span className="owner-registration__welcome-text--highlight">naszym zespole</span>
                        </h2>
                    </div>
                </div>
                <div className="owner-registration__right-panel">
                    <div className="owner-registration__form-wrapper">
                        <h1 className="owner-registration__title">Error</h1>
                        <p className="owner-registration__error">Nieprawidłowy lub brakujący token rejestracyjny.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="owner-registration">
            <div className="owner-registration__left-panel">
                <div className="owner-registration__left-content">
                    <h2 className="owner-registration__welcome-text">
                        Witamy w
                        <span className="owner-registration__welcome-text--highlight">naszym zespole</span>
                    </h2>
                </div>
            </div>
            <div className="owner-registration__right-panel">
                <button className="owner-registration__back-btn" onClick={handleBack}>
                    ← wróć
                </button>
                <div className="owner-registration__form-wrapper">
                    <h1 className="owner-registration__title">Zarejestruj się jako właściciel</h1>
                    <form className="owner-registration__form" onSubmit={handleSubmit}>
                        <div className="owner-registration__form-row">
                            <div className="owner-registration__field-group">
                                <input
                                    type="password"
                                    name="password"
                                    placeholder="Hasło"
                                    className={`owner-registration__input ${fieldErrors.password ? 'owner-registration__input--error' : ''}`}
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.password && <span className="owner-registration__field-error">{fieldErrors.password}</span>}
                            </div>
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="firstName"
                                    placeholder="Imię"
                                    className={`owner-registration__input ${fieldErrors.firstName ? 'owner-registration__input--error' : ''}`}
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.firstName && <span className="owner-registration__field-error">{fieldErrors.firstName}</span>}
                            </div>
                        </div>

                        <div className="owner-registration__form-row">
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="lastName"
                                    placeholder="Nazwisko"
                                    className={`owner-registration__input ${fieldErrors.lastName ? 'owner-registration__input--error' : ''}`}
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.lastName && <span className="owner-registration__field-error">{fieldErrors.lastName}</span>}
                            </div>
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="phone"
                                    placeholder="Numer telefonu"
                                    className={`owner-registration__input ${fieldErrors.phone ? 'owner-registration__input--error' : ''}`}
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.phone && <span className="owner-registration__field-error">{fieldErrors.phone}</span>}
                            </div>
                        </div>

                        <div className="owner-registration__form-row">
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="country"
                                    placeholder="Kraj"
                                    className={`owner-registration__input ${fieldErrors.country ? 'owner-registration__input--error' : ''}`}
                                    value={formData.country}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.country && <span className="owner-registration__field-error">{fieldErrors.country}</span>}
                            </div>
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="city"
                                    placeholder="Miasto"
                                    className={`owner-registration__input ${fieldErrors.city ? 'owner-registration__input--error' : ''}`}
                                    value={formData.city}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.city && <span className="owner-registration__field-error">{fieldErrors.city}</span>}
                            </div>
                        </div>

                        <div className="owner-registration__form-row">
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="postalCode"
                                    placeholder="Kod pocztowy"
                                    className={`owner-registration__input ${fieldErrors.postalCode ? 'owner-registration__input--error' : ''}`}
                                    value={formData.postalCode}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.postalCode && <span className="owner-registration__field-error">{fieldErrors.postalCode}</span>}
                            </div>
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="street"
                                    placeholder="Ulica"
                                    className={`owner-registration__input ${fieldErrors.street ? 'owner-registration__input--error' : ''}`}
                                    value={formData.street}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.street && <span className="owner-registration__field-error">{fieldErrors.street}</span>}
                            </div>
                        </div>

                        <div className="owner-registration__form-row">
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="buildingNumber"
                                    placeholder="Numer budynku"
                                    className={`owner-registration__input ${fieldErrors.buildingNumber ? 'owner-registration__input--error' : ''}`}
                                    value={formData.buildingNumber}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.buildingNumber && <span className="owner-registration__field-error">{fieldErrors.buildingNumber}</span>}
                            </div>
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="apartmentNumber"
                                    placeholder="Numer mieszkania"
                                    className="owner-registration__input"
                                    value={formData.apartmentNumber}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="owner-registration__checkbox-group">
                            <label className="owner-registration__checkbox-label">
                                <input
                                    type="checkbox"
                                    name="statuteAccepted"
                                    checked={formData.statuteAccepted}
                                    onChange={handleChange}
                                    required
                                    className="owner-registration__checkbox"
                                />
                                <span>Akceptuję <a href="https://petboard.pl/regulamin/" target="_blank" rel="noopener noreferrer">regulamin aplikacji</a></span>
                            </label>
                            {fieldErrors.statuteAccepted && <span className="owner-registration__field-error">{fieldErrors.statuteAccepted}</span>}
                        </div>

                        {error && <div className="owner-registration__error">{error}</div>}
                        {success && <div className="owner-registration__success">{success}</div>}

                        <button type="submit" className="owner-registration__submit-btn">
                            Zarejestruj się
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RegisterOwner;